import { fetchNextAvailable, LegacyClinic } from '@packages/cortex-sdk';
import { timeZone } from '@packages/shared';
import { useQueries } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';

export function useNextAvailablePerClinic(
  clinics: LegacyClinic[],
  serviceId: string,
  practitionerId: string,
) {
  const results = useQueries({
    queries: clinics.map((clinic) => ({
      queryKey: ['next-available', clinic.id, serviceId, practitionerId],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchNextAvailable({
          clinicId: clinic.id,
          serviceId,
          practitionerId,
          signal,
        }),
      enabled: !!serviceId && !!practitionerId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const clinicLoadingStates = new Map(
    clinics.map((clinic, i) => [clinic.id, results[i].isLoading]),
  );

  const completedLoadingNextAvailableForAllClinics =
    results.length > 0 && results.every((q) => q.isSuccess || q.isError);

  const availabilityMap = useMemo(() => {
    const map = new Map<string, number>();
    if (completedLoadingNextAvailableForAllClinics) {
      clinics.forEach((clinic, i) => {
        const data = results[i].data;
        if (data) {
          const zonedDate = utcToZonedTime(parseISO(data.startTime), timeZone);
          map.set(clinic.id, zonedDate.getTime());
        }
      });
    }
    return map;
  }, [completedLoadingNextAvailableForAllClinics, clinics, results]);

  return {
    clinicLoadingStates,
    completedLoadingNextAvailableForAllClinics,
    availabilityMap,
  };
}
